'use client';

import { Button, DialogFooter } from '@shared/components/shadcn';
import { FieldMessage } from '../form-controls/FieldMessage/FieldMessage';
import { ToggleGroup } from '../form-controls/ToggleGroup/ToggleGroup';
import { ContinueButton } from '../Forms/Registration/components/ContinueButton/ContinueButton';
import { StepLayout } from '../Forms/Registration/components/StepLayout/StepLayout';
import type { ChannelPreferencesActionsProps, ChannelPreferencesProps } from './types';

function ChannelPreferencesActions({
  onContinue,
  onSkipOffers,
  continueButtonText,
  skipButtonText,
  continueButtonTrackName,
  continueButtonTrackingProps,
  selectedCount,
  footerContainerElement: FooterWrapper = 'div',
  footerContainerProps = {},
}: ChannelPreferencesActionsProps) {
  return (
    <FooterWrapper {...footerContainerProps} data-track-container="Actions">
      <ContinueButton onClick={onContinue} data-track-name={continueButtonTrackName ?? (selectedCount === 0 ? 'All Options' : 'Selected Options')} {...continueButtonTrackingProps}>
        {continueButtonText}
      </ContinueButton>
      <Button onClick={onSkipOffers} variant="secondary" data-track-name="No Options" type="button" data-testid="no-options-button">
        {skipButtonText}
      </Button>
    </FooterWrapper>
  );
}

function ChannelPreferences({
  value,
  onChange,
  options,
  error,
  onContinue,
  onSkipOffers,
  continueButtonText,
  skipButtonText,
  continueButtonTrackName,
  continueButtonTrackingProps,
  fieldId = 'channelPreferences-error',
  fieldTrackHelper,
  stepLayout,
  className = '',
  addFooterElement = false,
}: ChannelPreferencesProps) {
  const selectedCount = value?.length ?? 0;
  const content = (
    <div className={'flex flex-col w-full min-h-0 flex-1 md:flex-none gap-8'}>
      <div className="flex flex-col align-center">
        <ToggleGroup value={value ?? []} onValueChange={onChange} options={options} hasError={!!error} className="mt-2" data-track-container="Options" />
        <FieldMessage id={fieldId} message={error} messageType={error ? 'error' : undefined} data-track-helper={fieldTrackHelper} />
      </div>

      <ChannelPreferencesActions
        onContinue={onContinue}
        onSkipOffers={onSkipOffers}
        continueButtonText={continueButtonText}
        skipButtonText={skipButtonText}
        continueButtonTrackName={continueButtonTrackName}
        continueButtonTrackingProps={continueButtonTrackingProps}
        selectedCount={selectedCount}
        footerContainerElement={addFooterElement ? DialogFooter : 'div'}
        footerContainerProps={addFooterElement ? { className: 'flex-col' } : { className: 'flex flex-col gap-4 mt-auto md:mt-0' }}
      />
    </div>
  );

  if (stepLayout) {
    return (
      <StepLayout {...stepLayout} className={className}>
        {content}
      </StepLayout>
    );
  }

  return content;
}

export { ChannelPreferences, ChannelPreferencesActions };
